import { useGetHaInstanceStateQuery } from '@monorepo/shared';
import { t } from 'i18next';
import { FC, useEffect, useState } from 'react';
import { Button, Message, Modal, Stack, useToaster } from 'rsuite';
import { dataStyle } from './AccountDetail';
interface IProps {
  btnText: string;
  title: string;
  content: string;
  callBackFn: any;
  disabled?: boolean;
}
export const InstanceConfirmBtn = ({
  btnText,
  title,
  content,
  disabled,
  callBackFn
}: IProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Button
        appearance="ghost"
        size="xs"
        onClick={handleOpen}
        disabled={disabled}
      >
        {btnText}
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{content}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              if (callBackFn) {
                callBackFn();
              }
              handleClose();
            }}
            appearance="primary"
          >
            Confirm
          </Button>
          <Button onClick={handleClose} appearance="subtle">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
interface IErrorProps {
  toaster: any;
  error: any;
  title?: string;
}

export const ErrorToaster = ({ toaster, error, title }: IErrorProps) => {
  const res = error as Record<string, any>;
  const code = res?.data?.error_code || res?.status || '';
  const message =
    res?.data?.error_desc ||
    res?.data?.message ||
    res?.data?.detail ||
    res?.data?.details ||
    '';
  const errMessage = (
    <Message showIcon type="warning" closable header={title}>
      <div>
        {/* <span> {code ? `${message}: [${code}]` : `${message}`}</span> */}
        <div>
          {code && (
            <Stack spacing={12}>
              <b>Error Code:</b> <span>{code}</span>
            </Stack>
          )}
        </div>
        <div>
          {message && (
            <Stack spacing={12}>
              <b>Detail:</b> <span>{message}</span>
            </Stack>
          )}
        </div>
      </div>
    </Message>
  );
  toaster.push(errMessage, {
    placement: 'topCenter',
    duration: 5000
  });
};

const StatusText = (data: Record<string, any>) => {
  const callBackFn = data?.data?.callBackFn;
  const actionText: Record<string | number, string> = {
    3: 'Reboot',
    4: 'Creating Snapshot',
    5: 'Reverting Snapshot'
  };
  useEffect(() => {
    if (
      data.data.status === 'ACTIVE' ||
      data.data?.status === 'SUCCESS' ||
      data.data?.status === 'ERROR'
    ) {
      if (callBackFn) {
        callBackFn({ data: data.data });
      }
    }
  }, [data.data?.status]);

  return (
    <Stack spacing={12}>
      <div style={dataStyle}>{actionText[data.data?.arg?.action] || ''}</div>
      <div className="dot-flashing"></div>
    </Stack>
  );
};
interface StatusObj {
  action: string | number;
  account_id: string;
  instance_id: string;
  region: Record<string, string>;
  license_sn: string;
  user_id?: string | number;
  access_from_user_id?: string | number;
  group_id?: string;
  callBackFn?: any;
}
const CheckStatus =
  (Component: FC<any>) =>
  ({
    action,
    instance_id,
    account_id,
    user_id,
    access_from_user_id,
    group_id,
    license_sn,
    region,
    callBackFn
  }: StatusObj) => {
    const [isActive, SetIsActive] = useState<boolean>(true);
    const [data, setData] = useState<Record<string, any>>({});
    const toaster = useToaster();
    useEffect(() => {
      if (region?.token && action && instance_id && account_id) {
        SetIsActive(true);
      }
    }, [action, region]);
    const { data: instanceStatus, error: statusError } =
      useGetHaInstanceStateQuery(
        {
          region,
          instance_id,
          account_id,
          user_id,
          access_from_user_id,
          group_id,
          license_sn,
          action
        },
        {
          skip: !isActive,
          pollingInterval: 10000, // 10 seconds
          refetchOnMountOrArgChange: true
        }
      );

    useEffect(() => {
      if (
        instanceStatus?.status === 'ACTIVE' ||
        instanceStatus?.status === 'SUCCESS'
      ) {
        SetIsActive(false);
      }
      if (statusError || instanceStatus?.status === 'ERROR') {
        const errorMessage = `Get Instance [instance id: ${instance_id}] Status Error`;
        setData({
          status: 'ERROR',
          error_desc: errorMessage
        });
        ErrorToaster({
          toaster,
          error: statusError || {
            status: 'ERROR',
            data: {
              message: errorMessage
            }
          },
          title: 'Instance Status Error!'
        });
        SetIsActive(false);
      } else {
        setData({
          status: instanceStatus?.status
        });
      }
    }, [instanceStatus, statusError]);
    return (
      <Component
        data={{
          ...data,
          arg: { instance_id, region, account_id, action, license_sn },
          callBackFn
        }}
      />
    );
  };

export const InstanceStatus = CheckStatus(StatusText);
